
import Vue from "vue";
import { AuthService } from "@/services/auth-service";
import { AccountantService, IAccountantDossier, IAccountantInvite, IExactDossierConfig } from "@/services/accountant-service";
import { ExactSyncService } from "@/services/exact-sync-service";
import { ClearfactsSyncService } from "@/services/clearfacts-sync-service";
import AppUblOverview from "@/components/bookkeeping/UblOverview.vue";
import AppExportToExactModal from "@/components/accountant/ExportToExactModal.vue";
import AppExportToWinbooksModal from "@/components/accountant/ExportToWinbooksModal.vue";
import AppExportToYukiModal from "@/components/accountant/ExportToYukiModal.vue";
import AppExportToClearfactsModal from "@/components/accountant/ExportToClearfactsModal.vue";
import AppExactSyncLogModal from "@/components/accountant/ExactSyncLogModal.vue";
import AppExactUnsyncedModal from "@/components/accountant/ExactUnsyncedModal.vue";
import AppInviteClientModal from "@/components/accountant/InviteClientModal.vue";

export default Vue.extend({
	data() {
		return {
			dossiers: [] as IAccountantDossier[],
			invites: [] as IAccountantInvite[],
			activeTab: 0,
			inviteModal: false,
			cancelLoadingId: null as number | null,
			ublModal: false,
			ublDossier: null as IAccountantDossier | null,
			ublLoadingId: null as number | null,
			exactModal: false,
			exactDossier: null as IAccountantDossier | null,
			exactLoadingId: null as number | null,
			winbooksModal: false,
			winbooksDossier: null as IAccountantDossier | null,
			winbooksLoadingId: null as number | null,
			yukiModal: false,
			yukiDossier: null as IAccountantDossier | null,
			clearfactsModal: false,
			clearfactsDossier: null as IAccountantDossier | null,
			clearfactsLoadingId: null as number | null,
			yukiLoadingId: null as number | null,
			syncLogModal: false,
			syncLogDossier: null as IAccountantDossier | null,
			unsyncedModal: false,
			unsyncedDossier: null as IAccountantDossier | null,
			testRunLoadingId: null as number | null,
			openMenuId: null as number | null
		};
	},
	computed: {
		tabs():string[] {
			return [this.$ct("tabs.clients").toString(), this.$ct("tabs.invited").toString()];
		},
		clientDossiers():IAccountantDossier[] {
			return this.dossiers.filter(d => !d.IsDemo);
		}
	},
	methods: {
		async loadDossiers() {
			this.dossiers = await AccountantService.getDossiers();
		},
		async loadInvites() {
			this.invites = await AccountantService.getInvites();
		},
		formatDate(date:string):string {
			if (!date) return "";
			return new Date(date).toLocaleDateString();
		},
		async cancelInvite(i:IAccountantInvite) {
			try {
				this.cancelLoadingId = i.ID;
				await AccountantService.cancelInvite(i.ID);
				await this.loadInvites();
			} catch (err) {
				this.$wf.notify(this.$ct("cancel-invite-error").toString(), "error", 4000, "exclamation-triangle");
			} finally {
				this.cancelLoadingId = null;
			}
		},
		async openDossier(name:string) {
			await AuthService.selectDossier(name);
			this.$router.push("/");
		},
		goToSettings(d:IAccountantDossier) {
			this.$router.push(`/portal/settings/${d.DossierID}`);
		},
		createTicket(d:IAccountantDossier) {
			this.$router.push(`/portal/support/new?dossierId=${d.DossierID}`);
		},
		async createUblArchive(d:IAccountantDossier) {
			try {
				this.ublLoadingId = d.DossierID;
				await AuthService.selectDossier(d.DossierName);
				this.ublDossier = d;
				this.ublModal = true;
			} catch (err) {
				this.$wf.notify(this.$ct("ubl-archive-error").toString(), "error", 4000, "exclamation-triangle");
			} finally {
				this.ublLoadingId = null;
			}
		},
		async exportToExact(d:IAccountantDossier) {
			try {
				this.exactLoadingId = d.DossierID;
				await AuthService.selectDossier(d.DossierName);
				this.exactDossier = d;
				this.exactModal = true;
			} catch (err) {
				this.$wf.notify(this.$ct("exact-export-error").toString(), "error", 4000, "exclamation-triangle");
			} finally {
				this.exactLoadingId = null;
			}
		},
		async exportToWinbooks(d:IAccountantDossier) {
			try {
				this.winbooksLoadingId = d.DossierID;
				await AuthService.selectDossier(d.DossierName);
				this.winbooksDossier = d;
				this.winbooksModal = true;
			} catch (err) {
				this.$wf.notify(this.$ct("winbooks-export-error").toString(), "error", 4000, "exclamation-triangle");
			} finally {
				this.winbooksLoadingId = null;
			}
		},
		async exportToYuki(d:IAccountantDossier) {
			try {
				this.yukiLoadingId = d.DossierID;
				await AuthService.selectDossier(d.DossierName);
				this.yukiDossier = d;
				this.yukiModal = true;
			} catch (err) {
				this.$wf.notify(this.$ct("yuki-export-error").toString(), "error", 4000, "exclamation-triangle");
			} finally {
				this.yukiLoadingId = null;
			}
		},
		setMenu(id:number, open:boolean) {
			if (open) {
				this.openMenuId = id;
			} else if (this.openMenuId === id) {
				this.openMenuId = null;
			}
		},
		closeMenu() {
			this.openMenuId = null;
		},
		async exportToClearfacts(d:IAccountantDossier) {
			try {
				this.clearfactsLoadingId = d.DossierID;
				await AuthService.selectDossier(d.DossierName);
				this.clearfactsDossier = d;
				this.clearfactsModal = true;
			} catch (err) {
				this.$wf.notify(this.$ct("clearfacts-export-error").toString(), "error", 4000, "exclamation-triangle");
			} finally {
				this.clearfactsLoadingId = null;
			}
		},
		hasSyncLog(d:IAccountantDossier):boolean {
			return ["exact", "yuki", "clearfacts"].includes(d.IntegrationType);
		},
		openSyncLog(d:IAccountantDossier) {
			this.syncLogDossier = d;
			this.syncLogModal = true;
		},
		async openUnsynced(d:IAccountantDossier) {
			try {
				await AuthService.selectDossier(d.DossierName);
				this.unsyncedDossier = d;
				this.unsyncedModal = true;
			} catch (err) {
				this.$wf.notify(this.$ct("exact-export-error").toString(), "error", 4000, "exclamation-triangle");
			}
		},
		async testRunAutoSync(d:IAccountantDossier) {
			try {
				this.testRunLoadingId = d.DossierID;
				await AuthService.selectDossier(d.DossierName);
				let res = d.IntegrationType == "clearfacts"
					? await ClearfactsSyncService.runAutoSyncNow()
					: await ExactSyncService.runAutoSyncNow();
				let created = res.Sales.Created + res.Purchases.Created;
				let failed = res.Sales.Failed + res.Purchases.Failed;
				this.$wf.notify(
					this.$ct("test-run-result", {created, failed}).toString(),
					failed > 0 ? "warning" : "success",
					6000,
					failed > 0 ? "exclamation-triangle" : "check"
				);
				this.syncLogDossier = d;
				this.syncLogModal = true;
			} catch (err) {
				this.$wf.notify(this.$ct("test-run-error").toString(), "error", 5000, "exclamation-triangle");
			} finally {
				this.testRunLoadingId = null;
			}
		},
		integrationLabel(d:IAccountantDossier):string {
			if (d.IntegrationType == "exact") {
				let cfg = this.parseConfig<IExactDossierConfig>(d.Config);
				return cfg?.division_name ? this.$ct("badge.exact-with-division", {name: cfg.division_name}).toString() : this.$ct("badge.exact").toString();
			}
			if (d.IntegrationType == "clearfacts") return this.$ct("badge.clearfacts").toString();
			if (d.IntegrationType == "winbooks") return this.$ct("badge.winbooks").toString();
			if (d.IntegrationType == "yuki") return this.$ct("badge.yuki").toString();
			return this.$ct("badge.manual").toString();
		},
		badgeClass(d:IAccountantDossier):string {
			return `badge-${d.IntegrationType}`;
		},
		parseConfig<T>(raw:string):T | null {
			if (!raw) return null;
			try {
				return JSON.parse(raw) as T;
			} catch {
				return null;
			}
		}
	},
	async mounted() {
		let splashEl = document.getElementById("splash");
		if (splashEl) splashEl.remove();
		await Promise.all([this.loadDossiers(), this.loadInvites()]);
	},
	components: {
		AppUblOverview,
		AppExportToExactModal,
		AppExportToWinbooksModal,
		AppExportToYukiModal,
		AppExportToClearfactsModal,
		AppExactSyncLogModal,
		AppExactUnsyncedModal,
		AppInviteClientModal
	},
	i18nEx: {
		componentPrefix: "views.AccountantPortal"
	}
});
